import type { RumEvent, RumEventDomainContext } from '@datadog/browser-rum';
import { EnrichedRequest, EnrichedResponse, FetchContext, SanitizableData } from './datadogEventProcessor.types'

const SENSITIVE_HEADERS = ['authorization', 'x-api-key', 'api-key', 'auth-token', 'cookie', 'set-cookie'] as const;
const SENSITIVE_FIELDS = ['password', 'token', 'secret', 'credit_card', 'cvv', 'ssn'] as const;

const sanitizeHeaders = (headers: Record<string, string>): Record<string, string> => {
  const sanitized = { ...headers };
  Object.keys(sanitized).forEach(key => {
    if (SENSITIVE_HEADERS.some(sensitive => key.toLowerCase() === sensitive)) {
      sanitized[key] = '[REDACTED]';
    }
  });
  return sanitized;
};

const sanitizeObject = <T extends SanitizableData>(obj: T, depth = 0): T => {
  if (depth > 10 || !obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => sanitizeObject(item as SanitizableData, depth + 1)) as T;

  const sanitized: Record<string, SanitizableData> = {};
  Object.entries(obj).forEach(([key, value]) => {
    const isSensitive = SENSITIVE_FIELDS.some(field => key.toLowerCase().includes(field));

    if (isSensitive) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value as SanitizableData, depth + 1);
    } else {
      sanitized[key] = value as SanitizableData;
    }
  });
  return sanitized as T;
};

type TruncatedData = {
  _truncated: true;
  _originalLength: number;
  _value: string;
};

const truncateIfNeeded = <T>(value: T, maxLength = 10000): T | TruncatedData => {
  if (typeof value === 'string' && value.length > maxLength) {
    return {
      _truncated: true,
      _originalLength: value.length,
      _value: value.substring(0, maxLength) + '...',
    };
  }
  return value;
};

const safeParse = <T = unknown>(data: string | T): T => {
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data) as T;
  } catch {
    return data as T;
  }
};

const isFetchOrXhrContext = (context: RumEventDomainContext): context is FetchContext => {
  return 'requestInput' in context || 'response' in context;
};

const buildEnrichedRequest = (context: FetchContext): EnrichedRequest | undefined => {
  const { requestInit, requestInput } = context;
  if (!requestInit) return undefined;

  const request: EnrichedRequest = {
    url: requestInput,
    baseURL: requestInit.baseURL,
    method: requestInit.method,
    queryParams: requestInit.query,
  };

  if (requestInit.headers) {
    const headersObj =
      // eslint-disable-next-line no-nested-ternary
      requestInit.headers instanceof Headers
        ? Object.fromEntries(requestInit.headers.entries())
        : Array.isArray(requestInit.headers)
          ? Object.fromEntries(requestInit.headers)
          : (requestInit.headers as Record<string, string>);

    request.headers = sanitizeHeaders(headersObj);
  }

  if (requestInit.body) {
    const parsed = safeParse(requestInit.body as string);
    request.body = truncateIfNeeded(sanitizeObject(parsed as SanitizableData));
  }

  return request;
};

const buildEnrichedResponse = (context: FetchContext): EnrichedResponse | undefined => {
  const { response } = context;
  if (!response) return undefined;

  const responseData = response._data || response.data;
  const enrichedResponse: EnrichedResponse = {
    status: response.status,
  };

  if (response.headers) {
    const headersObj = Object.fromEntries(response.headers.entries());
    enrichedResponse.headers = sanitizeHeaders(headersObj);
  }

  if (responseData) {
    enrichedResponse.data = truncateIfNeeded(sanitizeObject(responseData as SanitizableData));
  }

  return enrichedResponse;
};

const enrichResourceData = (event: RumEvent, context: RumEventDomainContext): void => {
  if (event.type !== 'resource') return;

  if (!isFetchOrXhrContext(context)) return;

  const resourceEvent = event as RumEvent & {
    resource?: { type?: string; method?: string; status_code?: number };
    context?: Record<string, unknown>;
  };

  if (resourceEvent.resource?.type !== 'fetch' && resourceEvent.resource?.type !== 'xhr') return;

  const isGetRequest = resourceEvent.resource.method?.toUpperCase() === 'GET';
  const isError = (resourceEvent.resource.status_code ?? 0) >= 400;

  // FILTER: Ignores successful GETs, but keeps GETs with errors
  if (isGetRequest && !isError) return;

  if (!resourceEvent.context) {
    resourceEvent.context = {};
  }

  const request = buildEnrichedRequest(context);
  if (request) {
    resourceEvent.context.request = request;
  }

  const response = buildEnrichedResponse(context);
  if (response) {
    resourceEvent.context.response = response;
  }

  resourceEvent.context.enrichmentTimestamp = new Date().toISOString();
  if ((resourceEvent.resource?.status_code ?? 0) >= 400) {
    resourceEvent.context.hasError = true;
  }
};

const enrichErrorData = (event: RumEvent): void => {
  if (event.type !== 'error') return;

  const errorEvent = event as RumEvent & {
    error?: { resource?: { url?: string; method?: string } };
    context?: Record<string, unknown>;
  };

  if (errorEvent.error?.resource) {
    if (!errorEvent.context) {
      errorEvent.context = {};
    }

    errorEvent.context.errorInfo = {
      resourceUrl: errorEvent.error.resource.url,
      resourceMethod: errorEvent.error.resource.method,
    };
  }
};

export const processDatadogEvent = (event: RumEvent, context: RumEventDomainContext): boolean => {
  try {
    enrichResourceData(event, context);
    enrichErrorData(event);
    return true;
  } catch (e) {
    console.error('Error while processing Datadog event', e);
    return true;
  }
};
